import type { ServedTier } from "./dictionary.js";
import type { ToolElements } from "./hanzi-elements.js";
import { ToolSettings } from "./tool-settings.js";
import { TierLoading } from "./tier-loading.js";
import { TierOffer } from "./tier-offer.js";
import { readUrlState, type WritableState } from "./url-state.js";

/**
 * The tool's state: what it opens in, and what it is in now.
 *
 * Three sources, in order: the link somebody followed, what the last visit
 * stored on this device, and the defaults. A link says what it is for and
 * would be a strange thing to open and find showing something else, but it
 * only speaks for what it names, so anything it leaves out still comes from
 * this device.
 */
export interface Startup {
  readonly settings: ToolSettings;
  readonly tiers: TierLoading;

  /**
   * The dictionary a link asked for, or nothing where it did not ask.
   *
   * Undefined rather than the default, because the two are answered
   * differently now: a link naming a tier is obeyed, and a link naming none
   * leaves the tool free to open on whatever the device already has. Collapsing
   * them here would make `?dictionary=standard` unable to mean what it says.
   */
  readonly tier: ServedTier | undefined;
}

/**
 * Read the address bar, fill the input from it, and show the settings.
 */
export function startingState(
  elements: ToolElements,
  dictionaryUrl: string,
  onSettingsChanged: () => void,
): Startup {
  const fromUrl = readUrlState(location.search);

  if (fromUrl.text !== undefined) {
    elements.input.value = fromUrl.text;
  }

  return {
    tiers: new TierLoading({
      dictionaryUrl,
      status: elements.status,
      upgrade: new TierOffer({
        button: elements.upgrade,
        panel: elements.upgradePanel,
      }),
      revert: new TierOffer({
        button: elements.revert,
        panel: elements.revertPanel,
      }),
    }),
    settings: new ToolSettings(
      elements.controls,
      fromUrl.options,
      onSettingsChanged,
    ),
    tier: fromUrl.tier,
  };
}

/**
 * What the address bar says, and what Copy link copies.
 *
 * The text, the settings and the dictionary — everything somebody would want
 * back if they bookmarked this or sent it to a colleague.
 */
export function currentState(
  elements: ToolElements,
  settings: ToolSettings,
  tiers: TierLoading,
): WritableState {
  return {
    text: elements.input.value,
    options: settings.options,
    tier: tiers.tier,
  };
}
